/**
 * PeriodSelector
 * Compact Linear-style period picker for the PageToolbar tabs slot.
 * Layout: [◄] label ▾ [►]
 * Clicking the label opens a menu with periods grouped by year.
 */

import { useRef, useEffect, useState } from 'react';
import {
  ArrowDropDownIcon,
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  cn,
} from '@billda/design-system';
import type { PotPeriodSummaryDto } from '../api/potPeriodsApi';

interface PeriodsByYear {
  year: number;
  periods: PotPeriodSummaryDto[];
}

interface PeriodSelectorProps {
  label: string;
  shortLabel: string;
  periodsByYear: PeriodsByYear[];
  selection: {
    effectivePeriodId: string | null;
    onPeriodChange: (periodId: string) => void;
  };
  navigation: {
    onPrev: () => void;
    onNext: () => void;
    canGoPrev: boolean;
    canGoNext: boolean;
  };
  pagination?: {
    hasMoreYears?: boolean;
    onLoadMoreYears?: () => void;
  };
}

export function PeriodSelector({
  label,
  shortLabel,
  periodsByYear,
  selection,
  navigation,
  pagination,
}: PeriodSelectorProps) {
  const { effectivePeriodId, onPeriodChange } = selection;
  const { onPrev, onNext, canGoPrev, canGoNext } = navigation;
  const { hasMoreYears, onLoadMoreYears } = pagination ?? {};

  const [open, setOpen] = useState(false);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to selected period when the menu opens
  useEffect(() => {
    if (open && selectedItemRef.current) {
      // Small delay to ensure the menu has rendered
      requestAnimationFrame(() => {
        selectedItemRef.current?.scrollIntoView({
          block: 'center',
          behavior: 'instant',
        });
      });
    }
  }, [open]);

  const handleSelect = (periodId: string) => {
    onPeriodChange(periodId);
    setOpen(false);
  };

  return (
    <div
      data-testid="period-selector"
      className="-ml-1 flex items-center gap-0.5"
    >
      <Button
        variant="ghost"
        size="icon"
        className="size-7 transition-all duration-200"
        onClick={onPrev}
        disabled={!canGoPrev}
        aria-label="Previous period"
        data-testid="period-selector-prev"
      >
        <ChevronLeftIcon aria-hidden="true" className="size-5" />
      </Button>

      <Menu open={open} onOpenChange={setOpen}>
        <MenuTrigger asChild>
          <button
            type="button"
            data-testid="period-selector-toggle"
            className="flex items-center gap-1 rounded-lg px-2 py-1 transition-all duration-200 hover:bg-surface-raised"
          >
            {/* Full label on sm+, short on xs */}
            <span className="hidden type-body-md font-medium whitespace-nowrap sm:block">
              {label}
            </span>
            <span className="block type-body-md font-medium whitespace-nowrap sm:hidden">
              {shortLabel}
            </span>
            <ArrowDropDownIcon
              aria-hidden="true"
              className="size-[18px] text-content-secondary"
            />
          </button>
        </MenuTrigger>
        <MenuContent
          align="start"
          data-testid="period-selector-popover"
          className="max-h-90 min-w-70 overflow-y-auto"
        >
          {periodsByYear.map(({ year, periods }) => (
            <div key={year}>
              <div className="sticky top-0 z-10 bg-surface-raised px-4 pt-3 pb-1 type-overline font-semibold tracking-wide text-content-secondary">
                {year}
              </div>
              {periods.map(period => {
                const isSelected = period.id === effectivePeriodId;
                return (
                  <MenuItem
                    key={period.id}
                    ref={isSelected ? selectedItemRef : undefined}
                    onSelect={() => {
                      handleSelect(period.id);
                    }}
                    data-testid={`period-selector-menuitem-${period.id}`}
                    className={cn(
                      'py-1.5',
                      isSelected && 'bg-surface-selected'
                    )}
                  >
                    {period.displayLabel}
                  </MenuItem>
                );
              })}
            </div>
          ))}
          {hasMoreYears && onLoadMoreYears && (
            <div className="flex justify-center py-2">
              <Button
                size="sm"
                variant="ghost"
                className="type-caption"
                onClick={onLoadMoreYears}
                data-testid="period-selector-load-more"
              >
                Load older years...
              </Button>
            </div>
          )}
        </MenuContent>
      </Menu>

      <Button
        variant="ghost"
        size="icon"
        className="size-7 transition-all duration-200"
        onClick={onNext}
        disabled={!canGoNext}
        aria-label="Next period"
        data-testid="period-selector-next"
      >
        <ChevronRightIcon aria-hidden="true" className="size-5" />
      </Button>
    </div>
  );
}
